/**
 * @fileoverview NoteRow — single presentational row in a chronological
 * contact notes feed.
 *
 * Renders a TipTap-authored note with avatar, author name, relative
 * timestamp, and an "edited" indicator when the note has been touched
 * after creation. Authors get an inline Edit / Delete menu; everyone
 * else just reads.
 *
 * This component is scope-agnostic: it knows nothing about job vs firm
 * contacts or Convex. Persistence is delegated to the `onSave` / `onDelete`
 * callbacks supplied by the container, which keeps the same row usable for
 * both the job-contact and firm-contact feeds.
 *
 * Editing swaps the read-only `prose` body for a full `NotesEditor`
 * with Save / Cancel actions. We persist on Save (not autosave) so the
 * user has explicit control and discards on Cancel.
 */

import { useRef, useState } from "react";
import DOMPurify from "dompurify";
import type { Id } from "@cvx/_generated/dataModel";
import { MoreHorizontal, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/ui/primitives/avatar";
import { Button } from "@/ui/primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import {
  NotesEditor,
  type NotesEditorHandle,
} from "@/ui/composed/editor/notes-editor";
import { cn } from "@/utils/misc";

interface NoteAuthor {
  _id: Id<"users">;
  firstName?: string;
  lastName?: string;
  email?: string;
  avatarUrl?: string;
}

/**
 * Scope-agnostic note shape. `_id` is a plain string so the same row can
 * render `jobContactNotes` and `firmContactNotes` ids alike; the container
 * binds the id into its `onSave` / `onDelete` callbacks.
 */
export interface FeedNote {
  _id: string;
  content: string;
  createdBy: Id<"users">;
  createdAt: number;
  updatedAt?: number;
  authorDisplayName: string;
  lastEditedByName?: string;
  /**
   * Resolved avatar URL for the note's author, when the backing query
   * returns one. Feeds whose endpoint doesn't resolve avatars simply omit
   * it and the row falls back to initials.
   */
  authorAvatarUrl?: string;
}

interface NoteRowProps {
  note: FeedNote;
  /** Author of THIS specific note — for the avatar lookup. */
  author?: NoteAuthor;
  /** True when the current user is the note's author (controls Edit/Delete). */
  canEdit: boolean;
  /** True when the parent contact is read-only (e.g. merged / archived). */
  readOnly?: boolean;
  /** Persist an edited body. Rejects on failure so the row can toast. */
  onSave: (content: string) => Promise<void>;
  /** Soft-delete this note. Rejects on failure so the row can toast. */
  onDelete: () => Promise<void>;
}

function initialsFor(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2);
  if (parts.length === 0) return "?";
  return parts.join("").toUpperCase();
}

function formatRelative(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatAbsolute(ts: number): string {
  return new Date(ts).toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function NoteRow({
  note,
  author,
  canEdit,
  readOnly = false,
  onSave,
  onDelete,
}: NoteRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(note.content);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const editorRef = useRef<NotesEditorHandle | null>(null);

  const handleSave = async () => {
    const content = draft;
    if (content === note.content) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    try {
      await onSave(content);
      toast.success("Note updated");
      setIsEditing(false);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not save the note";
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(note.content);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete();
      toast.success("Note deleted");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not delete the note";
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const editedAt =
    note.updatedAt !== undefined && note.updatedAt > note.createdAt
      ? note.updatedAt
      : null;
  const initials = initialsFor(note.authorDisplayName);

  return (
    <article className="rounded-lg border border-border bg-background p-3">
      <header className="mb-2 flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Avatar className="h-7 w-7 shrink-0">
            {author?.avatarUrl && (
              <AvatarImage
                src={author.avatarUrl}
                alt={note.authorDisplayName}
              />
            )}
            <AvatarFallback className="text-[10px] font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-medium leading-tight">
              {note.authorDisplayName}
            </span>
            <span
              className="text-[10px] text-muted-foreground"
              title={formatAbsolute(note.createdAt)}
            >
              {formatRelative(note.createdAt)}
              {editedAt !== null && (
                <>
                  {" · "}
                  <span title={`Edited ${formatAbsolute(editedAt)}`}>
                    edited {formatRelative(editedAt)}
                  </span>
                </>
              )}
            </span>
          </div>
        </div>
        {canEdit && !readOnly && !isEditing && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                aria-label="Note actions"
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-36">
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setIsEditing(true);
                }}
              >
                <Pencil className="mr-2 h-3.5 w-3.5" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onSelect={(e) => {
                  e.preventDefault();
                  void handleDelete();
                }}
                disabled={isDeleting}
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </header>

      {isEditing ? (
        <div className="flex flex-col gap-2">
          <NotesEditor
            ref={editorRef}
            value={draft}
            onChange={setDraft}
            placeholder="Edit the note…"
            minHeightClass="min-h-[120px]"
          />
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => void handleSave()}
              disabled={isSaving}
            >
              {isSaving ? (
                <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
              ) : null}
              Save
            </Button>
          </div>
        </div>
      ) : (
        <div
          className={cn(
            "prose prose-sm max-w-none text-foreground dark:prose-invert",
          )}
          // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- stored note HTML is DOMPurify.sanitize'd here at the render boundary
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note.content) }}
        />
      )}
    </article>
  );
}
